import { ChevronDown, Database, Info, Settings, Tag, Zap } from 'lucide-react';
import { useState } from 'react';
import useAtelierCreator from '../../stores/useAtelierCreator';

// ── TOKENS (comme dans CaseCreator)
const tokens = (dm) => ({
  textSub: dm ? 'text-zinc-400' : 'text-zinc-500',
  textMuted: dm ? 'text-zinc-600' : 'text-zinc-400',
  input: dm
    ? 'bg-zinc-900 border-zinc-700 text-zinc-100 placeholder:text-zinc-600 focus:border-indigo-400/60 focus:ring-1 focus:ring-indigo-400/20'
    : 'bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 shadow-sm',
  accentBg: dm ? 'bg-indigo-500/10' : 'bg-indigo-50',
  accentBorder: dm ? 'border-indigo-500/25' : 'border-indigo-200',
});

// ── FieldLabel (comme dans CaseCreator)
function FieldLabel({ label, hint, required, darkMode }) {
  return (
    <div className="mb-1.5">
      <div className="flex items-center gap-1.5">
        <label className={`text-[11px] font-bold uppercase tracking-widest ${darkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>
          {label}
        </label>
        {required && (
          <span className="text-[9px] font-bold text-red-400 border border-red-400/30 px-1 rounded-sm">requis</span>
        )}
      </div>
      {hint && (
        <p className={`text-[11px] mt-0.5 leading-snug ${darkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>{hint}</p>
      )}
    </div>
  );
}

// ── WorkspaceCard (comme dans CaseCreator)
function WorkspaceCard({ title, subtitle, icon: Icon, accent = 'indigo', required = false, children, darkMode, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const dm = darkMode;

  const accentColors = {
    indigo: { border: 'border-l-indigo-400', icon: 'text-indigo-500' },
    emerald: { border: 'border-l-emerald-400', icon: 'text-emerald-500' },
    sky: { border: 'border-l-sky-400', icon: 'text-sky-500' },
  };
  const ac = accentColors[accent] || accentColors.indigo;

  return (
    <div className={`rounded-lg border transition-all overflow-hidden ${
      open
        ? `${ac.border} border-l-2 border-y ${dm ? 'border-white/8 bg-zinc-900/40' : 'border-zinc-200 bg-white shadow-sm'}`
        : `border-l-2 border-l-transparent border-y ${dm ? 'border-white/5' : 'border-zinc-100'}`
    }`}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={`w-full flex items-center justify-between px-5 py-4 transition-colors ${dm ? 'hover:bg-white/3' : 'hover:bg-zinc-50/70'}`}
      >
        <div className="flex items-start gap-3">
          <div className={`mt-0.5 p-1.5 rounded-md ${dm ? 'bg-zinc-800' : 'bg-zinc-100'}`}>
            <Icon size={14} className={open ? ac.icon : dm ? 'text-zinc-500' : 'text-zinc-400'} />
          </div>
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className={`text-[13px] font-semibold ${open ? (dm ? 'text-white' : 'text-zinc-900') : (dm ? 'text-zinc-500' : 'text-zinc-500')}`}>{title}</span>
              {required && (
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${open ? 'text-red-400 border-red-400/30 bg-red-400/5' : 'text-zinc-500 border-zinc-600'}`}>REQUIS</span>
              )}
            </div>
            {subtitle && open && <p className={`text-[11px] mt-0.5 ${dm ? 'text-zinc-500' : 'text-zinc-400'}`}>{subtitle}</p>}
          </div>
        </div>
        <ChevronDown size={14} className={`flex-shrink-0 transition-transform ${dm ? 'text-zinc-600' : 'text-zinc-400'} ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className={`px-5 pb-5 border-t ${dm ? 'border-white/5' : 'border-zinc-100'}`}>
          <div className="pt-4">{children}</div>
        </div>
      )}
    </div>
  );
}

// ── CONSTANTES ─────────────────────────────────────────────────────────────
const LEVELS = ['Accessible', 'Intermédiaire', 'Avancé', 'Tous niveaux'];
const ACCENTS = ['#22d3ee', '#f97316', '#a855f7', '#10b981', '#fb7185', '#f59e0b', '#ec4899', '#6366f1'];
const ICONS = ['🎓', '🧡', '✍️', '🎑', '🏡', '🎨', '🥋', '👨‍👩‍👧‍👦', '💃', '📚', '🌱', '🔬'];

const KNOWN_TEMPLATES = [
  { slug: 'bac-revision-v1', label: 'Bac Révision v1' },
  { slug: 'orientation-identite', label: 'Orientation & Identité' },
  { slug: 'assistante-maternelle-identite-v1', label: 'Ass. Maternelle v1' },
  { slug: 'assistante-maternelle-identite-v2', label: 'Ass. Maternelle v2' },
  { slug: 'education-societe', label: 'Éducation & Société' },
];

// ── COMPOSANT PRINCIPAL ────────────────────────────────────────────────────
export default function Section1_Identite({ darkMode }) {
  const dm = darkMode;
  const tk = tokens(dm);
  const inputCls = `${tk.input} border outline-none transition-all rounded-lg px-3 py-2.5 text-[13px] w-full`;

  const { formData, errors, mappings, updateFormData, updateMapping, interfaceMode } = useAtelierCreator();
  const isSimple = interfaceMode === 'simplifie';

  const handleTag = (raw) => {
    const tags = raw.split(',').map(s => s.trim()).filter(Boolean);
    updateFormData('tags', tags);
  };

  const genSlug = () => {
    const base = (formData.title || 'atelier').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    updateFormData('slug', `${base}-${Date.now().toString(36)}`);
  };

  const isInMapping = (templateSlug) => {
    const ids = mappings[templateSlug] || [];
    return ids.includes(formData.id || formData.slug);
  };

  const toggleMapping = (templateSlug) => {
    const ids = [...(mappings[templateSlug] || [])];
    const atelId = formData.id || formData.slug;
    const idx = ids.indexOf(atelId);
    if (idx === -1) ids.push(atelId);
    else ids.splice(idx, 1);
    updateMapping(templateSlug, ids);
  }; 

  return (
    <div className="space-y-4 animate-in fade-in duration-500">
      
      {/* Header section — comme CaseCreator */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className={`text-2xl font-bold tracking-tight ${dm ? 'text-white' : 'text-zinc-900'}`}>
            {isSimple ? 'Identité de l\'atelier' : 'Informations générales'}
          </h2>
          <p className={`text-[12px] mt-1 ${tk.textSub}`}>
            {isSimple ? 'Les informations de base de votre atelier' : 'Configuration système racine et métadonnées'}
          </p>
        </div>
        <div className={`text-[10px] font-mono px-2 py-1 rounded border ${dm ? 'border-zinc-700 text-zinc-500' : 'border-zinc-200 text-zinc-400'}`}>
          {isSimple ? 'MODE SIMPLIFIÉ' : 'MODE EXPERT'}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-5">
        
        {/* ── COLONNE GAUCHE 7/12 ── */}
        <div className="col-span-12 lg:col-span-7 space-y-4">

          {/* Identité — REQUIS */}
          <WorkspaceCard title="Identité de l'atelier" subtitle="Nom, code unique et description" icon={Database} accent="indigo" required darkMode={dm} defaultOpen>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <FieldLabel label={isSimple ? 'Code unique' : 'Identifiant technique'} hint="Courte chaîne sans espaces" required darkMode={dm} />
                  <input value={formData.slug || ''} onChange={e => updateFormData('slug', e.target.value)} placeholder={isSimple ? 'ex: atelier-orientation' : 'ATELIER_CORE_01'} className={inputCls} />
                  {errors?.slug && <p className="text-red-400 text-[11px] mt-1">{errors.slug}</p>}
                </div>
                <div>
                  <FieldLabel label={isSimple ? 'Titre' : 'Label principal'} required darkMode={dm} />
                  <input value={formData.title || ''} onChange={e => updateFormData('title', e.target.value)} placeholder={isSimple ? "Préparation Post-Bac" : 'Entrer le label...'} className={inputCls} />
                  {errors?.title && <p className="text-red-400 text-[11px] mt-1">{errors.title}</p>}
                </div>
              </div>
              <div>
                <FieldLabel label={isSimple ? 'Sous-titre' : 'Référence système'} darkMode={dm} />
                <input value={formData.subtitle || ''} onChange={e => updateFormData('subtitle', e.target.value)} placeholder={isSimple ? 'Orientation & Projet de Vie' : 'ATELIER_ALPHA'} className={inputCls} />
              </div>
              <div>
                <FieldLabel label="Description" hint={isSimple ? 'Résumé visible par les apprenants' : 'Description technique du système'} required darkMode={dm} />
                <textarea value={formData.description || ''} onChange={e => updateFormData('description', e.target.value)} rows={3} placeholder={isSimple ? 'Décrivez brièvement ce que les apprenants vont découvrir...' : 'Description technique du système'} className={`${inputCls} resize-none`} />
                {errors?.description && <p className="text-red-400 text-[11px] mt-1">{errors.description}</p>}
              </div>
            </div>
          </WorkspaceCard>

          {/* Options générales */}
          <WorkspaceCard title="Options générales" subtitle="Niveau, durée et publication" icon={Settings} accent="sky" darkMode={dm} defaultOpen={false}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FieldLabel label={isSimple ? 'Niveau' : 'Complexité'} darkMode={dm} />
                <select value={formData.level || ''} onChange={e => updateFormData('level', e.target.value)} className={inputCls + ' appearance-none'}>
                  <option value="">Sélectionner...</option>
                  {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <FieldLabel label={isSimple ? 'Durée (minutes)' : 'Temps estimé'} darkMode={dm} />
                <input type="number" value={formData.duration || ''} onChange={e => updateFormData('duration', Number(e.target.value))} className={inputCls} placeholder="90" />
              </div>
              <div className="col-span-2">
                <label className={`flex items-center gap-3 cursor-pointer p-3 rounded-lg transition-colors ${dm ? 'hover:bg-zinc-800' : 'hover:bg-zinc-50'}`}>
                  <input type="checkbox" checked={formData.isPublished || false} onChange={e => updateFormData('isPublished', e.target.checked)} className="w-4 h-4 accent-indigo-500 rounded" />
                  <div>
                    <span className={`text-[12px] font-medium ${dm ? 'text-zinc-300' : 'text-zinc-700'}`}>{isSimple ? 'Publier l\'atelier' : 'Prêt au déploiement'}</span>
                    {isSimple && <p className={`text-[11px] mt-0.5 ${tk.textMuted}`}>Rend l'atelier accessible aux apprenants</p>}
                  </div>
                </label>
              </div>
            </div>
          </WorkspaceCard>

          {/* Tags */}
          <WorkspaceCard title="Tags & catégories" subtitle="Mots-clés pour la recherche" icon={Tag} accent="emerald" darkMode={dm} defaultOpen={false}>
            <div className="space-y-4">
              <div>
                <FieldLabel label="Tags" hint="Séparés par des virgules" darkMode={dm} />
                <input value={(formData.tags || []).join(', ')} onChange={e => handleTag(e.target.value)} className={inputCls} placeholder="scolaire, orientation, bac" />
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(formData.tags || []).map((tag, i) => tag && (
                    <span key={i} className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-md flex items-center gap-1 ${dm ? 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20' : 'bg-indigo-50 text-indigo-600 border-indigo-200'}`}>
                      {tag}
                      <button type="button" onClick={() => updateFormData('tags', formData.tags.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-300 ml-1">×</button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </WorkspaceCard>
        </div>

        {/* ── COLONNE DROITE 5/12 ── */}
        <div className="col-span-12 lg:col-span-5 space-y-4">

          {/* Apparence */}
          <WorkspaceCard title="Apparence" subtitle="Icône et couleur d'accent" icon={Zap} accent="emerald" darkMode={dm} defaultOpen>
            <div className="space-y-4">
              {/* Preview card */}
              <div className={`p-4 rounded-lg border relative overflow-hidden`} style={{ borderColor: `${formData.accentColor || '#6366f1'}40`, background: `${formData.accentColor || '#6366f1'}08` }}>
                <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: `linear-gradient(90deg, ${formData.accentColor || '#6366f1'}70, transparent)` }} />
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{formData.icon || '🎓'}</span>
                  <div>
                    <div className={`text-xs font-bold ${dm ? 'text-white' : 'text-zinc-900'}`}>{formData.title || 'Titre atelier'}</div>
                    <div className={`text-[10px] font-mono ${dm ? 'opacity-40' : 'text-zinc-400'}`}>{formData.subtitle || 'Sous-titre'}</div>
                  </div>
                </div>
              </div>

              {/* Icône picker */}
              <div>
                <FieldLabel label="Icône" darkMode={dm} />
                <div className="flex flex-wrap gap-2">
                  {ICONS.map(ic => (
                    <button key={ic} type="button" onClick={() => updateFormData('icon', ic)} className={`text-lg p-1.5 rounded-md border transition-all ${formData.icon === ic ? dm ? 'border-indigo-500/60 bg-indigo-500/10' : 'border-indigo-400 bg-indigo-50' : dm ? 'border-white/5 hover:border-white/20' : 'border-zinc-200 hover:border-zinc-300'}`}>
                      {ic}
                    </button>
                  ))}
                </div>
              </div>

              {/* Couleur accent */}
              <div>
                <FieldLabel label="Couleur accent" darkMode={dm} />
                <div className="flex flex-wrap gap-2 mb-2">
                  {ACCENTS.map(c => (
                    <button key={c} type="button" onClick={() => updateFormData('accentColor', c)} className={`w-6 h-6 rounded-md border-2 transition-all ${formData.accentColor === c ? 'border-white scale-110' : 'border-transparent'}`} style={{ background: c }} />
                  ))}
                </div>
                <input value={formData.accentColor || '#6366f1'} onChange={e => updateFormData('accentColor', e.target.value)} className={`${inputCls} text-center`} placeholder="#6366f1" />
              </div>
            </div>
          </WorkspaceCard>

          {/* Features live */}
          <WorkspaceCard title="Fonctionnalités live" subtitle="Outils disponibles pendant l'atelier" icon={Settings} accent="sky" darkMode={dm} defaultOpen={false}>
            <div className="space-y-3">
              {[
                { key: 'videoCallEnabled', label: 'Visio' },
                { key: 'whiteboardEnabled', label: 'Tableau blanc' },
                { key: 'notesEnabled', label: 'Notes partagées' },
              ].map(({ key, label }) => (
                <label key={key} className={`flex items-center justify-between p-2 rounded-lg cursor-pointer ${dm ? 'hover:bg-zinc-800' : 'hover:bg-zinc-50'}`}>
                  <span className={`text-[12px] ${dm ? 'text-zinc-400' : 'text-zinc-600'}`}>{label}</span>
                  <input type="checkbox" checked={!!formData[key]} onChange={e => updateFormData(key, e.target.checked)} className="accent-indigo-500 w-4 h-4 rounded" />
                </label>
              ))}
            </div>
          </WorkspaceCard>

          {/* Mapping déblocage */}
          <WorkspaceCard title="Prérequis" subtitle="Ateliers à compléter avant" icon={Settings} accent="emerald" darkMode={dm} defaultOpen={false}>
            <div className="space-y-2">
              <p className={`text-[10px] font-mono uppercase tracking-widest mb-2 ${tk.textMuted}`}>Débloqué par :</p>
              {KNOWN_TEMPLATES.map(t => (
                <label key={t.slug} className={`flex items-center gap-3 px-3 py-2 rounded-md border cursor-pointer transition-all ${isInMapping(t.slug) ? dm ? 'border-indigo-500/40 bg-indigo-500/5 text-indigo-300' : 'border-indigo-300 bg-indigo-50 text-indigo-700' : dm ? 'border-white/5 text-slate-500 hover:border-white/20' : 'border-zinc-200 text-zinc-500 hover:border-zinc-300'}`}>
                  <input type="checkbox" checked={isInMapping(t.slug)} onChange={() => toggleMapping(t.slug)} className="w-3.5 h-3.5 accent-indigo-500 rounded" />
                  <span className="text-[11px] font-mono uppercase tracking-wider">{t.label}</span>
                </label>
              ))}
            </div>
          </WorkspaceCard>
        </div>
      </div>

      {/* Conseil en mode simplifié */}
      {isSimple && (
        <div className={`p-4 rounded-xl border flex items-start gap-3 ${dm ? 'bg-indigo-500/5 border-indigo-500/20' : 'bg-indigo-50 border-indigo-200'}`}>
          <Info size={14} className="text-indigo-400 flex-shrink-0 mt-0.5" />
          <p className={`text-[12px] leading-relaxed ${dm ? 'text-indigo-300/80' : 'text-indigo-700'}`}>
            <strong>Conseil :</strong> Commencez par remplir le titre et la description. Les options avancées sont disponibles dans l'onglet "Options générales".
          </p>
        </div>
      )}
    </div>
  );
}